// Bloques B/C/D/E/F/G de 008 (Fase 3, Tanda 17, ver PLAN.md). Un solo
// componente — a diferencia de 018A/019 no hace falta partir por punto de
// inserción en el JSX porque `Formulario008.tsx` (Tanda 18) inserta este
// bloque completo de una vez, antes de H.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { SiNoBotones } from './camposSimples';
import { leerSiNo } from './camposSimplesDatos';
import {
  ACCIDENTE_VIOLENCIA_OPCIONES,
  ANTECEDENTES_CATEGORIAS_OPCIONES,
  CONDICION_EDAD_OPCIONES,
  CONDICION_LLEGADA_OPCIONES,
  ESTADO_CIVIL_OPCIONES,
  FORMA_LLEGADA_OPCIONES,
  SEGURO_SALUD_OPCIONES,
  TIPO_DOCUMENTO_OPCIONES,
  totalGlasgow008,
  type CondicionEdad,
  type CondicionLlegada,
  type EstadoBloqueB008,
  type EstadoBloqueC008,
  type EstadoBloqueD008,
  type EstadoBloqueE008,
  type EstadoBloqueG008,
  type EstadoBloques008Ingreso,
  type EstadoCivil,
  type FormaLlegada,
  type SeguroSalud,
  type TipoDocumento,
} from './bloques008IngresoDatos';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoNumero({ id, label, value, onChange, max }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly max?: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={0} max={max} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

export interface Bloques008IngresoProps {
  readonly estado: EstadoBloques008Ingreso;
  readonly onChange: (estado: EstadoBloques008Ingreso) => void;
}

function BloqueB({ estado, onChange }: { readonly estado: EstadoBloqueB008; readonly onChange: (v: EstadoBloqueB008) => void }): JSX.Element {
  const { t } = useTranslation();

  function actualizar(cambios: Partial<EstadoBloqueB008>): void {
    onChange({ ...estado, ...cambios });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueB.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f008-b-fecha-admision">{t('formularios.f008.bloqueB.fechaAdmision')}</Label>
          <Input id="f008-b-fecha-admision" type="date" value={estado.fechaAdmision} onChange={(e) => actualizar({ fechaAdmision: e.target.value })} />
        </div>
        <CampoTexto id="f008-b-admisionista" label={t('formularios.f008.bloqueB.admisionista')} value={estado.nombreAdmisionista} onChange={(v) => actualizar({ nombreAdmisionista: v })} />
        <CampoSelect<TipoDocumento>
          id="f008-b-tipo-documento"
          label={t('formularios.f008.bloqueB.tipoDocumento')}
          value={estado.tipoDocumento}
          opciones={TIPO_DOCUMENTO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueB.tipoDocumentoOpciones.${op}`)}
          onChange={(v) => actualizar({ tipoDocumento: v })}
        />
        <CampoTexto id="f008-b-primer-apellido" label={t('formularios.f008.bloqueB.primerApellido')} value={estado.primerApellido} onChange={(v) => actualizar({ primerApellido: v })} />
        <CampoTexto id="f008-b-segundo-apellido" label={t('formularios.f008.bloqueB.segundoApellido')} value={estado.segundoApellido} onChange={(v) => actualizar({ segundoApellido: v })} />
        <CampoTexto id="f008-b-primer-nombre" label={t('formularios.f008.bloqueB.primerNombre')} value={estado.primerNombre} onChange={(v) => actualizar({ primerNombre: v })} />
        <CampoTexto id="f008-b-segundo-nombre" label={t('formularios.f008.bloqueB.segundoNombre')} value={estado.segundoNombre} onChange={(v) => actualizar({ segundoNombre: v })} />
        <CampoSelect<EstadoCivil>
          id="f008-b-estado-civil"
          label={t('formularios.f008.bloqueB.estadoCivil')}
          value={estado.estadoCivil}
          opciones={ESTADO_CIVIL_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueB.estadoCivilOpciones.${op}`)}
          onChange={(v) => actualizar({ estadoCivil: v })}
        />
        <CampoTexto id="f008-b-sexo" label={t('formularios.f008.bloqueB.sexo')} value={estado.sexo} onChange={(v) => actualizar({ sexo: v })} />
        <CampoTexto id="f008-b-telefono-fijo" label={t('formularios.f008.bloqueB.telefonoFijo')} value={estado.telefonoFijo} onChange={(v) => actualizar({ telefonoFijo: v })} />
        <CampoTexto id="f008-b-telefono-celular" label={t('formularios.f008.bloqueB.telefonoCelular')} value={estado.telefonoCelular} onChange={(v) => actualizar({ telefonoCelular: v })} />
        <div className="flex flex-col gap-1">
          <Label htmlFor="f008-b-fecha-nacimiento">{t('formularios.f008.bloqueB.fechaNacimiento')}</Label>
          <Input id="f008-b-fecha-nacimiento" type="date" value={estado.fechaNacimiento} onChange={(e) => actualizar({ fechaNacimiento: e.target.value })} />
        </div>
        <CampoTexto id="f008-b-lugar-nacimiento" label={t('formularios.f008.bloqueB.lugarNacimiento')} value={estado.lugarNacimiento} onChange={(v) => actualizar({ lugarNacimiento: v })} />
        <CampoTexto id="f008-b-nacionalidad" label={t('formularios.f008.bloqueB.nacionalidad')} value={estado.nacionalidad} onChange={(v) => actualizar({ nacionalidad: v })} />
        <CampoNumero id="f008-b-edad" label={t('formularios.f008.bloqueB.edad')} value={estado.edad} onChange={(v) => actualizar({ edad: v })} />
        <CampoSelect<CondicionEdad>
          id="f008-b-condicion-edad"
          label={t('formularios.f008.bloqueB.condicionEdad')}
          value={estado.condicionEdad}
          opciones={CONDICION_EDAD_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueB.condicionEdadOpciones.${op}`)}
          onChange={(v) => actualizar({ condicionEdad: v })}
        />
        <SiNoBotones id="f008-b-historia-clinica" etiqueta={t('formularios.f008.bloqueB.historiaClinicaEnEstablecimiento')} valor={leerSiNo(estado.historiaClinicaEnEstablecimiento)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => actualizar({ historiaClinicaEnEstablecimiento: v })} />
        <SiNoBotones id="f008-b-grupo-prioritario" etiqueta={t('formularios.f008.bloqueB.grupoPrioritario')} valor={leerSiNo(estado.grupoPrioritario)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => actualizar({ grupoPrioritario: v })} />
        <CampoTexto
          id="f008-b-grupo-prioritario-especifique"
          label={t('formularios.f008.bloqueB.grupoPrioritarioEspecifique')}
          value={estado.grupoPrioritarioEspecifique}
          onChange={(v) => actualizar({ grupoPrioritarioEspecifique: v })}
        />
        <CampoTexto id="f008-b-autoidentificacion-etnica" label={t('formularios.f008.bloqueB.autoidentificacionEtnica')} value={estado.autoidentificacionEtnica} onChange={(v) => actualizar({ autoidentificacionEtnica: v })} />
        <CampoTexto id="f008-b-nacionalidad-etnica" label={t('formularios.f008.bloqueB.nacionalidadEtnica')} value={estado.nacionalidadEtnica} onChange={(v) => actualizar({ nacionalidadEtnica: v })} />
        <CampoTexto id="f008-b-pueblos" label={t('formularios.f008.bloqueB.pueblos')} value={estado.pueblos} onChange={(v) => actualizar({ pueblos: v })} />
        <CampoTexto id="f008-b-nivel-educacion" label={t('formularios.f008.bloqueB.nivelEducacion')} value={estado.nivelEducacion} onChange={(v) => actualizar({ nivelEducacion: v })} />
        <CampoTexto id="f008-b-estado-nivel-educacion" label={t('formularios.f008.bloqueB.estadoNivelEducacion')} value={estado.estadoNivelEducacion} onChange={(v) => actualizar({ estadoNivelEducacion: v })} />
        <CampoTexto id="f008-b-tipo-empresa-trabajo" label={t('formularios.f008.bloqueB.tipoEmpresaTrabajo')} value={estado.tipoEmpresaTrabajo} onChange={(v) => actualizar({ tipoEmpresaTrabajo: v })} />
        <CampoTexto id="f008-b-ocupacion-profesion" label={t('formularios.f008.bloqueB.ocupacionProfesion')} value={estado.ocupacionProfesion} onChange={(v) => actualizar({ ocupacionProfesion: v })} />
        <CampoSelect<SeguroSalud>
          id="f008-b-seguro-salud"
          label={t('formularios.f008.bloqueB.seguroSalud')}
          value={estado.seguroSalud}
          opciones={SEGURO_SALUD_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueB.seguroSaludOpciones.${op}`)}
          onChange={(v) => actualizar({ seguroSalud: v })}
        />
        <CampoTexto id="f008-b-provincia" label={t('formularios.f008.bloqueB.provincia')} value={estado.provincia} onChange={(v) => actualizar({ provincia: v })} />
        <CampoTexto id="f008-b-canton" label={t('formularios.f008.bloqueB.canton')} value={estado.canton} onChange={(v) => actualizar({ canton: v })} />
        <CampoTexto id="f008-b-parroquia" label={t('formularios.f008.bloqueB.parroquia')} value={estado.parroquia} onChange={(v) => actualizar({ parroquia: v })} />
        <CampoTexto id="f008-b-barrio-sector" label={t('formularios.f008.bloqueB.barrioSector')} value={estado.barrioSector} onChange={(v) => actualizar({ barrioSector: v })} />
        <CampoTexto id="f008-b-calle-principal" label={t('formularios.f008.bloqueB.callePrincipal')} value={estado.callePrincipal} onChange={(v) => actualizar({ callePrincipal: v })} />
        <CampoTexto id="f008-b-calle-secundaria" label={t('formularios.f008.bloqueB.calleSecundaria')} value={estado.calleSecundaria} onChange={(v) => actualizar({ calleSecundaria: v })} />
        <CampoTexto id="f008-b-referencia-domicilio" label={t('formularios.f008.bloqueB.referenciaDomicilio')} value={estado.referenciaDomicilio} onChange={(v) => actualizar({ referenciaDomicilio: v })} />
        <CampoTexto id="f008-b-contacto-nombre" label={t('formularios.f008.bloqueB.contactoNombre')} value={estado.contactoNombre} onChange={(v) => actualizar({ contactoNombre: v })} />
        <CampoTexto id="f008-b-contacto-parentesco" label={t('formularios.f008.bloqueB.contactoParentesco')} value={estado.contactoParentesco} onChange={(v) => actualizar({ contactoParentesco: v })} />
        <CampoTexto id="f008-b-contacto-direccion" label={t('formularios.f008.bloqueB.contactoDireccion')} value={estado.contactoDireccion} onChange={(v) => actualizar({ contactoDireccion: v })} />
        <CampoTexto id="f008-b-contacto-telefono" label={t('formularios.f008.bloqueB.contactoTelefono')} value={estado.contactoTelefono} onChange={(v) => actualizar({ contactoTelefono: v })} />
        <CampoSelect<FormaLlegada>
          id="f008-b-forma-llegada"
          label={t('formularios.f008.bloqueB.formaLlegada')}
          value={estado.formaLlegada}
          opciones={FORMA_LLEGADA_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueB.formaLlegadaOpciones.${op}`)}
          onChange={(v) => actualizar({ formaLlegada: v })}
        />
        <CampoTexto id="f008-b-fuente-informacion" label={t('formularios.f008.bloqueB.fuenteInformacion')} value={estado.fuenteInformacion} onChange={(v) => actualizar({ fuenteInformacion: v })} />
        <CampoTexto
          id="f008-b-institucion-o-persona-entrega"
          label={t('formularios.f008.bloqueB.institucionOPersonaEntrega')}
          value={estado.institucionOPersonaEntrega}
          onChange={(v) => actualizar({ institucionOPersonaEntrega: v })}
        />
        <CampoTexto id="f008-b-telefono-entrega" label={t('formularios.f008.bloqueB.telefonoEntrega')} value={estado.telefonoEntrega} onChange={(v) => actualizar({ telefonoEntrega: v })} />
      </div>
    </div>
  );
}

function BloqueC({ estado, onChange }: { readonly estado: EstadoBloqueC008; readonly onChange: (v: EstadoBloqueC008) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueC.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f008-c-fecha">{t('formularios.f008.bloqueC.fecha')}</Label>
          <Input id="f008-c-fecha" type="date" value={estado.fecha} onChange={(e) => onChange({ ...estado, fecha: e.target.value })} />
        </div>
        <CampoTexto id="f008-c-hora" label={t('formularios.f008.bloqueC.hora')} value={estado.hora} onChange={(v) => onChange({ ...estado, hora: v })} />
        <CampoSelect<CondicionLlegada>
          id="f008-c-condicion-llegada"
          label={t('formularios.f008.bloqueC.condicionLlegada')}
          value={estado.condicionLlegada}
          opciones={CONDICION_LLEGADA_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f008.bloqueC.condicionLlegadaOpciones.${op}`)}
          onChange={(v) => onChange({ ...estado, condicionLlegada: v })}
        />
      </div>
      <AreaDictado id="f008-c-motivo" etiqueta={t('formularios.f008.bloqueC.motivo')} valor={estado.motivo} onChange={(v) => onChange({ ...estado, motivo: v })} />
    </div>
  );
}

function BloqueD({ estado, onChange }: { readonly estado: EstadoBloqueD008; readonly onChange: (v: EstadoBloqueD008) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueD.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f008-d-fecha">{t('formularios.f008.bloqueD.fecha')}</Label>
          <Input id="f008-d-fecha" type="date" value={estado.fecha} onChange={(e) => onChange({ ...estado, fecha: e.target.value })} />
        </div>
        <CampoTexto id="f008-d-hora" label={t('formularios.f008.bloqueD.hora')} value={estado.hora} onChange={(v) => onChange({ ...estado, hora: v })} />
        <CampoTexto id="f008-d-lugar-evento" label={t('formularios.f008.bloqueD.lugarEvento')} value={estado.lugarEvento} onChange={(v) => onChange({ ...estado, lugarEvento: v })} />
        <CampoTexto id="f008-d-direccion-evento" label={t('formularios.f008.bloqueD.direccionEvento')} value={estado.direccionEvento} onChange={(v) => onChange({ ...estado, direccionEvento: v })} />
      </div>
      <div className="flex flex-wrap gap-4">
        <SiNoBotones id="f008-d-custodia-policial" etiqueta={t('formularios.f008.bloqueD.custodiaPolicial')} valor={leerSiNo(estado.custodiaPolicial)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ ...estado, custodiaPolicial: v })} />
        <SiNoBotones id="f008-d-notificacion" etiqueta={t('formularios.f008.bloqueD.notificacion')} valor={leerSiNo(estado.notificacion)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ ...estado, notificacion: v })} />
        <CampoCheckbox
          id="f008-d-sugestivo-aliento-alcoholico"
          label={t('formularios.f008.bloqueD.sugestivoAlientoAlcoholico')}
          checked={estado.sugestivoAlientoAlcoholico}
          onChange={(v) => onChange({ ...estado, sugestivoAlientoAlcoholico: v })}
        />
      </div>
      <ChecklistGrid claves={ACCIDENTE_VIOLENCIA_OPCIONES} prefijoI18n="formularios.f008.bloqueD.tipos.opciones" prefijoId="f008-d" valores={estado.tipos} onChange={(v) => onChange({ ...estado, tipos: v })} />
      <AreaDictado id="f008-d-observaciones" etiqueta={t('formularios.f008.bloqueD.observaciones')} valor={estado.observaciones} onChange={(v) => onChange({ ...estado, observaciones: v })} />
    </div>
  );
}

function BloqueE({ estado, onChange }: { readonly estado: EstadoBloqueE008; readonly onChange: (v: EstadoBloqueE008) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueE.titulo')}</h4>
      <CampoCheckbox id="f008-e-no-aplica" label={t('formularios.f008.bloqueE.noAplica')} checked={estado.noAplica} onChange={(v) => onChange({ ...estado, noAplica: v })} />
      <ChecklistGrid claves={ANTECEDENTES_CATEGORIAS_OPCIONES} prefijoI18n="formularios.f008.bloqueE.categorias.opciones" prefijoId="f008-e" valores={estado.categorias} onChange={(v) => onChange({ ...estado, categorias: v })} />
      <AreaDictado id="f008-e-describir" etiqueta={t('formularios.f008.bloqueE.describir')} valor={estado.describir} onChange={(v) => onChange({ ...estado, describir: v })} />
    </div>
  );
}

function BloqueF({ estado, onChange }: { readonly estado: string; readonly onChange: (v: string) => void }): JSX.Element {
  const { t } = useTranslation();
  return <AreaDictado id="f008-f-enfermedad-actual" etiqueta={t('formularios.f008.bloqueF.titulo')} valor={estado} onChange={onChange} />;
}

function BloqueG({ estado, onChange }: { readonly estado: EstadoBloqueG008; readonly onChange: (v: EstadoBloqueG008) => void }): JSX.Element {
  const { t } = useTranslation();
  const total = totalGlasgow008(estado);

  function actualizar(cambios: Partial<EstadoBloqueG008>): void {
    onChange({ ...estado, ...cambios });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f008.bloqueG.titulo')}</h4>
      <CampoCheckbox id="f008-g-sin-constantes" label={t('formularios.f008.bloqueG.sinConstantesVitales')} checked={estado.sinConstantesVitales} onChange={(v) => actualizar({ sinConstantesVitales: v })} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        <CampoTexto id="f008-g-presion-arterial" label={t('formularios.f008.bloqueG.presionArterial')} value={estado.presionArterial} onChange={(v) => actualizar({ presionArterial: v })} />
        <CampoNumero id="f008-g-pulso" label={t('formularios.f008.bloqueG.pulso')} value={estado.pulso} onChange={(v) => actualizar({ pulso: v })} />
        <CampoNumero id="f008-g-frecuencia-respiratoria" label={t('formularios.f008.bloqueG.frecuenciaRespiratoria')} value={estado.frecuenciaRespiratoria} onChange={(v) => actualizar({ frecuenciaRespiratoria: v })} />
        <CampoNumero id="f008-g-pulsioximetria" label={t('formularios.f008.bloqueG.pulsioximetria')} value={estado.pulsioximetria} onChange={(v) => actualizar({ pulsioximetria: v })} />
        <CampoNumero id="f008-g-perimetro-cefalico" label={t('formularios.f008.bloqueG.perimetroCefalico')} value={estado.perimetroCefalico} onChange={(v) => actualizar({ perimetroCefalico: v })} />
        <CampoNumero id="f008-g-peso" label={t('formularios.f008.bloqueG.peso')} value={estado.peso} onChange={(v) => actualizar({ peso: v })} />
        <CampoNumero id="f008-g-talla" label={t('formularios.f008.bloqueG.talla')} value={estado.talla} onChange={(v) => actualizar({ talla: v })} />
        <CampoNumero id="f008-g-glicemia-capilar" label={t('formularios.f008.bloqueG.glicemiaCapilar')} value={estado.glicemiaCapilar} onChange={(v) => actualizar({ glicemiaCapilar: v })} />
      </div>
      <div className="flex flex-col gap-2">
        <Label>
          {t('formularios.f008.bloqueG.glasgowTotal')}
          {total === null ? '' : `: ${String(total)}/15`}
        </Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CampoNumero id="f008-g-glasgow-ocular" label={t('formularios.f008.bloqueG.glasgowOcular')} value={estado.glasgowOcular} onChange={(v) => actualizar({ glasgowOcular: v })} max={4} />
          <CampoNumero id="f008-g-glasgow-verbal" label={t('formularios.f008.bloqueG.glasgowVerbal')} value={estado.glasgowVerbal} onChange={(v) => actualizar({ glasgowVerbal: v })} max={5} />
          <CampoNumero id="f008-g-glasgow-motora" label={t('formularios.f008.bloqueG.glasgowMotora')} value={estado.glasgowMotora} onChange={(v) => actualizar({ glasgowMotora: v })} max={6} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoTexto id="f008-g-reaccion-pupilar-derecha" label={t('formularios.f008.bloqueG.reaccionPupilarDerecha')} value={estado.reaccionPupilarDerecha} onChange={(v) => actualizar({ reaccionPupilarDerecha: v })} />
        <CampoTexto id="f008-g-reaccion-pupilar-izquierda" label={t('formularios.f008.bloqueG.reaccionPupilarIzquierda')} value={estado.reaccionPupilarIzquierda} onChange={(v) => actualizar({ reaccionPupilarIzquierda: v })} />
        <CampoTexto id="f008-g-tiempo-llenado-capilar" label={t('formularios.f008.bloqueG.tiempoLlenadoCapilar')} value={estado.tiempoLlenadoCapilar} onChange={(v) => actualizar({ tiempoLlenadoCapilar: v })} />
      </div>
    </div>
  );
}

export function Bloques008Ingreso({ estado, onChange }: Bloques008IngresoProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloqueB estado={estado.b} onChange={(v) => onChange({ ...estado, b: v })} />
      <BloqueC estado={estado.c} onChange={(v) => onChange({ ...estado, c: v })} />
      <BloqueD estado={estado.d} onChange={(v) => onChange({ ...estado, d: v })} />
      <BloqueE estado={estado.e} onChange={(v) => onChange({ ...estado, e: v })} />
      <BloqueF estado={estado.enfermedadActual} onChange={(v) => onChange({ ...estado, enfermedadActual: v })} />
      <BloqueG estado={estado.g} onChange={(v) => onChange({ ...estado, g: v })} />
    </div>
  );
}
